import { useCallback, useEffect, useRef, type MouseEvent, type PointerEvent } from 'react';

const LONG_PRESS_MS = 500;
/** Past this much finger travel the gesture is a scroll, not a press. */
const MOVE_CANCEL_PX = 10;

export interface LongPressHandlers {
  onPointerDown: (e: PointerEvent) => void;
  onPointerMove: (e: PointerEvent) => void;
  onPointerUp: () => void;
  onPointerCancel: () => void;
  onClickCapture: (e: MouseEvent) => void;
}

/**
 * Touch-only long press — the mobile stand-in for right-click.
 *
 * Two things make this more than a timer. A long press still produces a
 * `click` on release, and that click usually belongs to a parent handler, so
 * the press swallows it at the capture phase. And a press that turns into a
 * scroll must cancel, or flicking the tape copies.
 *
 * `handlersFor` takes the callback per call site so one hook instance serves
 * every row in a list. Because the timer is a single shared ref, a new
 * `pointerdown` always clears any timer left running by a prior press that
 * never resolved (multi-touch, or a fast drag across rows) — otherwise that
 * stale timeout would fire later against the wrong row's callback.
 */
export function useLongPressCopy(): {
  handlersFor: (onLongPress: () => void) => LongPressHandlers;
} {
  const timer = useRef<number | null>(null);
  const origin = useRef<{ x: number; y: number } | null>(null);
  const fired = useRef(false);

  const clear = useCallback((): void => {
    if (timer.current !== null) {
      window.clearTimeout(timer.current);
      timer.current = null;
    }
    origin.current = null;
  }, []);

  useEffect(
    () => () => {
      if (timer.current !== null) window.clearTimeout(timer.current);
    },
    [],
  );

  const handlersFor = useCallback(
    (onLongPress: () => void): LongPressHandlers => ({
      onPointerDown: (e: PointerEvent) => {
        if (e.pointerType !== 'touch') return;
        // Clear any timer left running by an overlapping press (multi-touch,
        // or a fast drag onto a new row without an intervening up/cancel) —
        // otherwise the stale timeout survives, unreachable by `clear()`,
        // and fires later against the wrong row's captured callback.
        if (timer.current !== null) {
          window.clearTimeout(timer.current);
          timer.current = null;
        }
        fired.current = false;
        origin.current = { x: e.clientX, y: e.clientY };
        timer.current = window.setTimeout(() => {
          fired.current = true;
          timer.current = null;
          onLongPress();
        }, LONG_PRESS_MS);
      },
      onPointerMove: (e: PointerEvent) => {
        if (timer.current === null || !origin.current) return;
        const dx = e.clientX - origin.current.x;
        const dy = e.clientY - origin.current.y;
        if (Math.hypot(dx, dy) > MOVE_CANCEL_PX) clear();
      },
      onPointerUp: clear,
      onPointerCancel: clear,
      onClickCapture: (e: MouseEvent) => {
        if (!fired.current) return;
        // The press already copied; keep the release from also acting.
        fired.current = false;
        e.stopPropagation();
        e.preventDefault();
      },
    }),
    [clear],
  );

  return { handlersFor };
}
